import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { PlanAccessManager } from '@/components/common/PlanAccess'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import {
  usePlanShares,
  useSharePlan,
  useUpdatePlanShareFreeze,
  useRemovePlanShare,
} from '@/hooks/usePlanShares'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'

interface SharePlanModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
}

export function SharePlanModal({
  open,
  onOpenChange,
  planId,
}: SharePlanModalProps) {
  const { userId, isAdmin } = useCurrentUser()
  const { data: sharesData, isLoading: isLoadingShares } = usePlanShares(planId)
  const shareMutation = useSharePlan(planId)
  const freezeMutation = useUpdatePlanShareFreeze(planId)
  const removeMutation = useRemovePlanShare(planId)

  const isOwner = !!userId && sharesData?.owner?.id === userId
  const canManage = isOwner || isAdmin

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Share Plan</DialogTitle>
          <DialogDescription>
            People you invite can view and edit this plan.
          </DialogDescription>
        </DialogHeader>

        <PlanAccessManager
          shares={sharesData}
          isLoadingShares={isLoadingShares}
          canManage={canManage}
          inviteLabel="Send Invite"
          isInviting={shareMutation.isPending}
          onInvite={(user, canFreeze, onDone) =>
            shareMutation.mutate(
              { user_id: String(user.id), can_freeze: canFreeze },
              {
                onSuccess: () => {
                  toast.success(
                    `Shared plan with ${user.email || user.display_name}`
                  )
                  onDone()
                },
                onError: error => {
                  toast.error(
                    `Failed to share: ${getErrorMessage(error, error.message)}`
                  )
                },
              }
            )
          }
          onToggleFreeze={(shareId, canFreeze) =>
            freezeMutation.mutate({ shareId, canFreeze })
          }
          togglingShareId={
            freezeMutation.isPending
              ? freezeMutation.variables?.shareId
              : undefined
          }
          onRemove={(shareId, onDone) =>
            removeMutation.mutate({ shareId }, { onSuccess: onDone })
          }
          isRemoving={removeMutation.isPending}
        />
      </DialogContent>
    </Dialog>
  )
}
